import { type SiteSlug, ThemeType, getTheme } from '@goodlifeplus/themes'
import type { OnFetchErrorContext } from '@vueuse/core'
import { type Ref, ref } from 'vue'
import { useToast } from 'vue-toastification'

import CustomerSafetyToast, {
  CUSTOMER_SAFETY_TOAST_OPTIONS,
} from './components/toasts/CustomerSafetyToast.vue'
import type { CustomerPlayExclusion, CustomerSpendingLimit } from './types'
import {
  isCustomerPlayExclusion,
  isCustomerSpendingLimit,
} from './utils/type-guard-utils'

export interface IPlayerSafetyException extends Error {
  data: Ref<PlayerSafetyError>
}

export type PlayerSafetyError = {
  message: string
  status: number
  error: CustomerSpendingLimit | CustomerPlayExclusion
}

/**
 * Represents a custom exception for handling player safety errors.
 */
export class PlayerSafetyException
  extends Error
  implements IPlayerSafetyException
{
  static readonly __brand: unique symbol = Symbol('PlayerSafetyException')
  public data: Ref<PlayerSafetyError>

  /**
   * Constructs a new PlayerSafetyException.
   *
   * @param {PlayerSafetyError} data - The error data.
   */
  constructor(data: PlayerSafetyError) {
    super(data.message)

    this.name = 'PlayerSafetyException'

    this.message = data.message

    this.data = ref(data)
    // Error.captureStackTrace(this, PlayerSafetyException)
  }

  /**
   * Determines if a given error context corresponds to a player safety error.
   *
   * @param {OnFetchErrorContext} ctx The error context from a fetch request.
   * @returns True if the error is related to player safety, false otherwise.
   */
  public static isPlayerSafetyError(ctx: OnFetchErrorContext): boolean {
    return (
      ctx.response?.status === 403 &&
      (isCustomerSpendingLimit(ctx.data) || isCustomerPlayExclusion(ctx.data))
    )
  }

  /**
   * Creates a PlayerSafetyException from a fetch error context if it corresponds to a player safety error.
   * Throws an error if the context does not correspond to a player safety error.
   *
   * @param {OnFetchErrorContext} ctx The error context from a fetch request.
   * @returns A PlayerSafetyException instance.
   */
  public static fromFetchErrorContext(
    ctx: OnFetchErrorContext
  ): PlayerSafetyException {
    if (!PlayerSafetyException.isPlayerSafetyError(ctx)) {
      throw new Error(
        'The provided context does not correspond to a player safety error.'
      )
    }

    const isSubscription =
      getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type ===
      ThemeType.Subscription
    const contact = !isSubscription
      ? import.meta.env.GLP_BUSINESS_PHONE
      : import.meta.env.GLP_BUSINESS_EMAIL || 'customer support'

    const errorData: PlayerSafetyError = {
      message: `Cannot complete purchase due to player safety limits. Please contact us on ${contact} for assistance.`,
      status: ctx.response?.status || 403,
      error: ctx.data as CustomerSpendingLimit | CustomerPlayExclusion,
    }

    return new PlayerSafetyException(errorData)
  }

  /**
   * Displays a toast notification to the user if the provided error context corresponds to a player safety error.
   */
  public showPlayerSafetyToast() {
    useToast()(
      {
        component: CustomerSafetyToast,
        props: {
          safetyMeasure: this.data.value.error,
        },
      },
      CUSTOMER_SAFETY_TOAST_OPTIONS
    )
  }
}
